import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Play, Square, AlertTriangle, Plus, TrendingUp, TrendingDown, Clock, Zap } from 'lucide-react';
import '../styles/TsmomR36Bot.css';

/**
 * R3.6 TSMOM Multi-Asset Bot Dashboard
 *
 * ACCESS: Admin only (Stage 1) | ENABLED_FOR: admin | TESTED: pending
 *
 * Weekly Vol-Scaled + Multi-Lookback Time-Series Momentum on BTC/ETH/SOL/BNB
 * HL perp. Signals computed from Binance perp 4h data (matches backtest).
 * Execution on Hyperliquid with a DEDICATED R3.6 wallet (env HYPERLIQUID_R36_*).
 * Fully isolated from V5.2 / V3 Gann bots — own DB tables, own scheduler job.
 * Telegram notifications via the shared TRADING_BOT_TELEGRAM channel.
 */
const API = process.env.REACT_APP_API_URL;
const ASSETS = ['BTC', 'ETH', 'SOL', 'BNB'];

async function api(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text.slice(0, 200)}`);
  }
  return res.json();
}

function formatNumber(v, d = 2) {
  if (v === null || v === undefined) return '—';
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

function formatPct(v, d = 1) {
  if (v === null || v === undefined) return '—';
  return `${(Number(v) * 100).toFixed(d)}%`;
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  } catch {
    return iso;
  }
}

/**
 * Live open-trade health panel.
 * Pure render from props — no state, no effects (rerender-derived-state-no-effect).
 * Shows entry/SL/current/unrealized PnL/bars-held so the operator can see at a
 * glance that the trade is being managed (was the gap that hid the no_entry_bar
 * incident on 2026-04-25).
 */
function OpenTradePanel({ trade }) {
  if (!trade) return null;
  const isLong = trade.side === 'long';
  const sideClass = isLong ? 'r36-side-long' : 'r36-side-short';
  const pnlUsd = trade.unrealized_pnl_usd;
  const pnlPct = trade.unrealized_pnl_pct;
  const pnlClass =
    pnlUsd === null || pnlUsd === undefined
      ? ''
      : pnlUsd >= 0
        ? 'r36-pnl-positive'
        : 'r36-pnl-negative';

  return (
    <div className="r36-open-trade-panel">
      <div className="r36-open-trade-header">
        <span className={`r36-open-trade-side ${sideClass}`}>
          {(trade.side || '').toUpperCase() || '—'}
        </span>
        <span className="r36-open-trade-id">#{trade.id}</span>
      </div>
      <div className="r36-open-trade-grid">
        <div>
          <label>Entry</label>
          <div>${formatNumber(trade.entry_price, 2)}</div>
        </div>
        <div>
          <label>Current</label>
          <div>{trade.current_price !== null && trade.current_price !== undefined ? `$${formatNumber(trade.current_price, 2)}` : '—'}</div>
        </div>
        <div>
          <label>SL</label>
          <div className="r36-sl-price">${formatNumber(trade.sl_price, 2)}</div>
        </div>
        <div>
          <label>Distance to SL</label>
          <div>{trade.distance_to_sl_pct !== null && trade.distance_to_sl_pct !== undefined ? `${trade.distance_to_sl_pct.toFixed(2)}%` : '—'}</div>
        </div>
        <div className={pnlClass}>
          <label>Unrealized P&amp;L</label>
          <div>
            {pnlUsd !== null && pnlUsd !== undefined ? `${pnlUsd >= 0 ? '+' : ''}$${formatNumber(Math.abs(pnlUsd), 2)}` : '—'}
            {pnlPct !== null && pnlPct !== undefined ? ` (${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(3)}%)` : ''}
          </div>
        </div>
        <div>
          <label>Bars held</label>
          <div>
            {trade.bars_held !== null && trade.bars_held !== undefined ? `${trade.bars_held}/${trade.hold_bars}` : `?/${trade.hold_bars}`}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, danger }) {
  const [typed, setTyped] = useState('');
  const required = danger ? 'GO LIVE' : null;
  const canConfirm = !required || typed === required;
  return (
    <div className="r36-modal-backdrop" onClick={onCancel}>
      <div className="r36-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className={danger ? 'r36-modal-title r36-danger' : 'r36-modal-title'}>
          {title}
        </h3>
        <div className="r36-modal-body">{message}</div>
        {required && (
          <div className="r36-modal-confirm">
            Type <code>{required}</code> to confirm:
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
            />
          </div>
        )}
        <div className="r36-modal-actions">
          <button onClick={onCancel}>Cancel</button>
          <button
            className={danger ? 'r36-btn-danger' : 'r36-btn-primary'}
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReadinessModal({ readiness, onClose, onConfirm }) {
  const checks = readiness?.checks || [];
  return (
    <div className="r36-modal-backdrop" onClick={onClose}>
      <div className="r36-modal r36-modal-wide" onClick={(e) => e.stopPropagation()}>
        <h3 className="r36-modal-title">
          Live Mode Readiness Check — {readiness?.symbol}
        </h3>
        <table className="r36-readiness-table">
          <tbody>
            {checks.map((c, i) => (
              <tr key={i} className={c.passed ? 'r36-pass' : 'r36-fail'}>
                <td>{c.passed ? '✅' : '❌'}</td>
                <td>{c.name}</td>
                <td className="r36-detail">{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {readiness?.ready ? (
          <div className="r36-ready-banner">
            All checks passed. You may switch to <b>LIVE</b> mode.
          </div>
        ) : (
          <div className="r36-notready-banner">
            Some checks failed. Fix them before switching to live.
          </div>
        )}
        <div className="r36-modal-actions">
          <button onClick={onClose}>Close</button>
          {readiness?.ready && (
            <button className="r36-btn-danger" onClick={onConfirm}>
              Continue to Live Switch →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function BotCard({ bot, onStart, onStop, onResume, onSwitchToReal, onForceTick, onCreate, assetBase, saving }) {
  if (!bot) {
    return (
      <div className="r36-card r36-card-empty">
        <div className="r36-card-header">
          <h3>{assetBase}</h3>
          <span className="r36-badge r36-badge-none">not created</span>
        </div>
        <div className="r36-card-body">
          <p>No R3.6 bot exists for {assetBase}.</p>
          <button className="r36-btn-primary" onClick={onCreate} disabled={saving}>
            <Plus size={14} /> Create {assetBase} bot (demo)
          </button>
        </div>
      </div>
    );
  }

  const state = bot.state || {};
  const modeBadge = bot.mode === 'real' ? 'live' : 'demo';
  const statusBadge = bot.status;

  return (
    <div className={`r36-card r36-card-${statusBadge}`}>
      <div className="r36-card-header">
        <h3>{bot.symbol}</h3>
        <div>
          <span className={`r36-badge r36-badge-${modeBadge}`}>
            {bot.mode.toUpperCase()}
          </span>
          <span className={`r36-badge r36-badge-${statusBadge}`}>
            {statusBadge}
          </span>
        </div>
      </div>

      <div className="r36-card-stats">
        <div className="r36-stat">
          <label>Capital</label>
          <div>${formatNumber(bot.capital)}</div>
        </div>
        <div className="r36-stat">
          <label>Closed trades</label>
          <div>{bot.closed_trades}</div>
        </div>
        <div className="r36-stat">
          <label>Open trade</label>
          <div>{bot.open_trade_id ? `#${bot.open_trade_id}` : '—'}</div>
        </div>
      </div>

      <div className="r36-votes-panel">
        <div className="r36-votes-header">Current votes (live)</div>
        <div className="r36-votes-row">
          <div className="r36-vote-chip r36-vote-long">
            <TrendingUp size={12} /> long: {state.votes_long ?? '—'}/4
          </div>
          <div className="r36-vote-chip r36-vote-short">
            <TrendingDown size={12} /> short: {state.votes_short ?? '—'}/4
          </div>
        </div>
        <div className="r36-vol-row">
          <span>Vol 30d: <b>{formatPct(state.realized_vol_ann, 0)}</b></span>
          <span>SL width: <b>{formatPct(state.sl_pct_if_fired, 1)}</b></span>
        </div>
        <div className="r36-eta">
          <Clock size={12} /> Next signal: <b>{formatTime(state.next_signal_eta)}</b>
        </div>
      </div>

      {bot.open_trade ? (
        <OpenTradePanel trade={bot.open_trade} />
      ) : null}

      {bot.halted_reason && (
        <div className="r36-halt">
          <AlertTriangle size={12} /> HALTED: {bot.halted_reason}
        </div>
      )}

      <div className="r36-card-actions">
        {statusBadge === 'stopped' && (
          <button className="r36-btn-primary" onClick={onStart} disabled={saving}>
            <Play size={12} /> Start
          </button>
        )}
        {statusBadge === 'running' && (
          <button onClick={onStop} disabled={saving}>
            <Square size={12} /> Stop
          </button>
        )}
        {statusBadge === 'halted' && (
          <button
            className="r36-btn-primary"
            onClick={onResume}
            disabled={saving}
            title="Clears the halt and re-arms the loss-streak breaker from this moment — pre-halt losses no longer count toward it."
          >
            <Play size={12} /> Resume
          </button>
        )}
        <button
          onClick={onForceTick}
          disabled={saving || statusBadge === 'halted'}
          title="Admin debug: trigger one tick immediately (bypass 15-min schedule). Does NOT place extra trades — only evaluates state and fires signals if it's a signal bar."
        >
          <Zap size={12} /> Force tick
        </button>
        {bot.mode === 'demo' && statusBadge !== 'running' && (
          <button
            className="r36-btn-warning"
            onClick={onSwitchToReal}
            disabled={saving}
            title="Switch to LIVE trading (runs readiness check first)"
          >
            Switch to LIVE →
          </button>
        )}
      </div>

      <div className="r36-last-tick">
        Last tick: {formatTime(bot.last_tick_time)}
      </div>
    </div>
  );
}

function TradeList({ trades }) {
  if (!trades || trades.length === 0) {
    return <div className="r36-empty">No trades yet.</div>;
  }
  return (
    <table className="r36-trades-table">
      <thead>
        <tr>
          <th>Asset</th><th>Side</th><th>Entry</th><th>Exit</th>
          <th>Reason</th><th>Net P&L</th><th>R</th>
        </tr>
      </thead>
      <tbody>
        {trades.slice(0, 50).map((t) => (
          <tr key={t.id} className={`r36-trade-${t.status}`}>
            <td>{t.symbol}</td>
            <td className={`r36-side-${t.side}`}>{t.side}</td>
            <td>${formatNumber(t.entry_price)}</td>
            <td>{t.exit_price ? `$${formatNumber(t.exit_price)}` : '—'}</td>
            <td>{t.exit_reason || 'open'}</td>
            <td className={Number(t.net_pnl) >= 0 ? 'r36-pnl-pos' : 'r36-pnl-neg'}>
              {t.net_pnl !== null ? `$${formatNumber(t.net_pnl)}` : '—'}
            </td>
            <td>{t.r_multiple ? Number(t.r_multiple).toFixed(2) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TsmomR36Bot({ userRole }) {
  const isAdmin = userRole === 'admin';

  const [status, setStatus] = useState({ bots: [] });
  const [allTrades, setAllTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per-bot saving state: null, bot_id (number), or 'new-<SYMBOL>' for create.
  // Prevents one card's in-flight action from disabling the other 3 cards'
  // buttons (which looked like "clicking Force Tick ticks all 4 assets").
  const [savingBotId, setSavingBotId] = useState(null);
  const [readinessForBot, setReadinessForBot] = useState(null);
  const [confirmGoLive, setConfirmGoLive] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const st = await api('/api/bot/tsmom-r36/status');
      setStatus(st);
      // Aggregate recent trades across all bots
      const tradesByBot = await Promise.all(
        (st.bots || []).map((b) =>
          api(`/api/bot/tsmom-r36/${b.bot_id}/trades?limit=25`)
            .then((rows) => rows.map((r) => ({ ...r, symbol: b.symbol })))
            .catch(() => [])
        )
      );
      const flat = tradesByBot.flat().sort((a, b) =>
        (b.entry_ts || '').localeCompare(a.entry_ts || '')
      );
      setAllTrades(flat);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const getBotForAsset = (asset) =>
    (status.bots || []).find((b) => b.symbol === asset);

  const create = async (asset) => {
    setSavingBotId(`new-${asset}`);
    try {
      await api('/api/bot/tsmom-r36/create', {
        method: 'POST',
        body: JSON.stringify({
          symbol: asset,
          initial_capital: 1000,
          leverage: 7,
          margin_pct_per_trade: 0.07,
        }),
      });
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  const startBot = async (bot) => {
    setSavingBotId(bot.bot_id);
    try {
      await api(`/api/bot/tsmom-r36/${bot.bot_id}/start`, { method: 'POST' });
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  const stopBot = async (bot) => {
    setSavingBotId(bot.bot_id);
    try {
      await api(`/api/bot/tsmom-r36/${bot.bot_id}/stop`, { method: 'POST' });
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  // Resume a HALTED bot. Distinct from start: /resume clears the halt AND
  // fences the consecutive-loss breaker at the resume moment (backend
  // config.resumed_at) — a plain /start would re-halt within one tick because
  // the breaker recomputes from the same pre-halt losses.
  const resumeBot = async (bot) => {
    setSavingBotId(bot.bot_id);
    try {
      await api(`/api/bot/tsmom-r36/${bot.bot_id}/resume`, { method: 'POST' });
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  const forceTick = async (bot) => {
    setSavingBotId(bot.bot_id);
    try {
      const r = await api(`/api/bot/tsmom-r36/${bot.bot_id}/force-tick`, { method: 'POST' });
      console.log('r36 force-tick:', r);
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  const checkReadiness = async (bot) => {
    setSavingBotId(bot.bot_id);
    try {
      const r = await api(`/api/bot/tsmom-r36/${bot.bot_id}/readiness`);
      setReadinessForBot({ ...r, bot_id: bot.bot_id });
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  const goLive = async () => {
    if (!readinessForBot) return;
    setSavingBotId(readinessForBot.bot_id);
    try {
      await api(`/api/bot/tsmom-r36/${readinessForBot.bot_id}/mode`, {
        method: 'POST',
        body: JSON.stringify({ mode: 'real' }),
      });
      setReadinessForBot(null);
      setConfirmGoLive(null);
      await refresh();
    } catch (e) { setError(e.message); }
    finally { setSavingBotId(null); }
  };

  // ACCESS: Admin only (Stage 1) — render nothing for non-admins
  if (!isAdmin) return null;
  if (loading) return <div className="r36-loading">Loading R3.6 status…</div>;

  return (
    <div className="r36-container">
      <div className="r36-header">
        <div>
          <h2><Activity size={16} /> R3.6 TSMOM Multi-Asset Bot</h2>
          <p className="r36-subtitle">
            Vol-Scaled + Multi-Lookback Weekly Momentum • Binance signal data
            • Hyperliquid execution • Telegram on shared channel
            • Auto-refresh every 30s
          </p>
        </div>
      </div>

      {error && <div className="r36-error">{error}</div>}

      <div className="r36-grid">
        {ASSETS.map((asset) => {
          const bot = getBotForAsset(asset);
          const cardSaving = bot
            ? savingBotId === bot.bot_id
            : savingBotId === `new-${asset}`;
          return (
            <BotCard
              key={asset}
              bot={bot}
              assetBase={asset}
              saving={cardSaving}
              onCreate={() => create(asset)}
              onStart={() => startBot(bot)}
              onStop={() => stopBot(bot)}
              onResume={() => resumeBot(bot)}
              onForceTick={() => forceTick(bot)}
              onSwitchToReal={() => checkReadiness(bot)}
            />
          );
        })}
      </div>

      <div className="r36-section">
        <h3>Recent trades across all assets</h3>
        <TradeList trades={allTrades} />
      </div>

      {readinessForBot && !confirmGoLive && (
        <ReadinessModal
          readiness={readinessForBot}
          onClose={() => setReadinessForBot(null)}
          onConfirm={() => setConfirmGoLive(readinessForBot)}
        />
      )}

      {confirmGoLive && (
        <ConfirmModal
          danger
          title="⚠️ Go LIVE on Hyperliquid?"
          message={
            <div>
              <p>
                About to switch <b>{confirmGoLive.symbol}</b> from <b>demo</b> to
                <b> REAL</b> mode. The next signal will place a live order on
                Hyperliquid using the R3.6 dedicated wallet.
              </p>
              <p>
                Every trade will be sized at <b>7% margin × 7× leverage</b> on
                your available HL balance.
              </p>
              <p><b>Type GO LIVE below to confirm.</b></p>
            </div>
          }
          confirmLabel="Switch to LIVE"
          onConfirm={goLive}
          onCancel={() => setConfirmGoLive(null)}
        />
      )}
    </div>
  );
}

export default TsmomR36Bot;
